import { RRule } from 'rrule';
import {
  currentTimeZone,
  formatToTime,
  formatToWeekDay,
  getDateTime,
} from 'infras/datetime';
import { RoutineScheduleItem } from 'presentations/api/schema/routine_schedule';

export interface ScheduleFormData {
  id?: number;
  year: number | null;
  yearInterval: number;
  month: number | null;
  monthInterval: number;
  wday: number | null;
  wdayInterval: number;
  day: number | null;
  dayInterval: number;
  hour: number;
  hourInterval: number;
  minute: number;
  minuteInterval: number;
  exceptions: string[] | null;
}

export function getRoutineScheduleString(
  schedules: ScheduleFormData[][],
  t: Function,
  isAllDay?: boolean,
) {
  return schedules.map((schedule, index) => {
    const firstSchedule = schedule[0];
    const firstScheduleTime = isAllDay
      ? t('Allday')
      : formatToTime(
          getDateTime({
            hour: firstSchedule.hour !== null ? firstSchedule.hour : undefined,
            minute:
              firstSchedule.minute !== null ? firstSchedule.minute : undefined,
          }),
        );
    switch (
      getRepeatUnit(
        firstSchedule.monthInterval,
        firstSchedule.dayInterval,
        firstSchedule.wdayInterval,
      )
    ) {
      case 'monthly':
        return `${
          firstSchedule.monthInterval === 1
            ? t('Every Month', { day: firstSchedule.day })
            : t('Month', {
                number: firstSchedule.monthInterval,
                day: firstSchedule.day,
              })
        } ${firstScheduleTime}`;
      case 'weekly':
        return `${
          firstSchedule.wdayInterval === 1
            ? t('Every Week')
            : t('Week', {
                number: firstSchedule.wdayInterval,
              })
        } ${schedule
          .map((s) => s.wday)
          .filter((wday): wday is number => typeof wday === 'number')
          .sort((a, b) => a - b)
          .map((wday) =>
            formatToWeekDay(
              getDateTime().set({
                weekday: wday === 0 ? 7 : wday,
              }),
            ),
          )
          .join(t('Week Join Format'))} ${firstScheduleTime}`;
      case 'daily':
        return `${
          firstSchedule.dayInterval === 1
            ? t('Every Day')
            : t('Day', {
                number: firstSchedule.dayInterval,
              })
        } ${firstScheduleTime}`;
      default:
        return '';
    }
  });
}

export function getRepeatUnit(
  monthInterval: number,
  dayInterval: number,
  wdayInterval: number,
) {
  let repeatUnit: string = 'monthly';
  if (monthInterval > 0 && wdayInterval > 0) {
    repeatUnit = 'weekly';
  } else if (monthInterval > 0 && dayInterval > 0) {
    repeatUnit = 'daily';
  }
  return repeatUnit;
}

export function cronDateToFormDate(timeFormat: any) {
  const zone = currentTimeZone();
  return getDateTime({ ...timeFormat, zone: 'utc' }).setZone(zone);
}

export function convertToScheduleFormData(
  routineSchedules: RoutineScheduleItem[],
) {
  const schedules: ScheduleFormData[][] = [];

  let prevRepeatUnit: string = '';
  let prevWdayInterval: number | null = null;
  let prevHour: number | null = null;
  let prevMinute: number | null = null;
  routineSchedules.forEach((rs) => {
    // utc -> 現在のタイムゾーンへ
    const wday = cronDateToFormDate({
      weekday: rs.wday === 0 ? 7 : rs.wday,
      hour: rs.hour,
      minute: rs.minute,
    }).weekday;
    let day: number | null = rs.day;
    if (rs.day !== null) {
      const timezoneOffset = new Date().getTimezoneOffset();
      const minute = (rs.hour || 0) * 60 + (rs.minute || 0);
      const hourUtc = (minute - timezoneOffset) / 60;
      if (hourUtc < 0) {
        day = rs.day - 1;
      } else if (hourUtc >= 24) {
        day = rs.day + 1;
        if (day === 0) {
          day = 1;
        }
      }
    }

    const routineSchedule: RoutineScheduleItem = {
      ...rs,
      day,
      wday: wday === 7 ? 0 : wday,
      hour: cronDateToFormDate({
        hour: rs.hour,
      }).hour,
      minute: cronDateToFormDate({
        minute: rs.minute,
      }).minute,
      exceptions: null,
    };

    const repeatUnit = getRepeatUnit(
      routineSchedule.monthInterval,
      routineSchedule.dayInterval,
      routineSchedule.wdayInterval,
    );

    // weeklyが続いていて、wdayIntervalやhour、minuteが同じであれば同じスケジュールとして扱う
    if (
      repeatUnit === 'weekly' &&
      prevRepeatUnit === 'weekly' &&
      schedules.length > 0 &&
      routineSchedule.wdayInterval === prevWdayInterval &&
      routineSchedule.hour === prevHour &&
      routineSchedule.minute === prevMinute
    ) {
      schedules[schedules.length - 1].push(routineSchedule);
    } else {
      schedules.push([routineSchedule]);
    }
    prevRepeatUnit = repeatUnit;
    prevWdayInterval = routineSchedule.wdayInterval;
    prevHour = routineSchedule.hour;
    prevMinute = routineSchedule.minute;
  });
  return schedules;
}

export function convertToUtcSchedules(
  schedules: ScheduleFormData[][],
  timeZone: string,
) {
  return schedules
    .flatMap((schedule) => schedule)
    .map((schedule) => {
      // 現在のタイムゾーン -> utcへ
      const wday =
        schedule.wday !== null
          ? getDateTime({
              weekday: schedule.wday === 0 ? 7 : schedule.wday,
              hour: schedule.hour || undefined,
              minute: schedule.minute || undefined,
            }).toUTC().weekday
          : null;

      let day: number | null = schedule.day;
      if (schedule?.day) {
        const timezoneOffset = getDateTime().setZone(timeZone).offset;
        const minute = (schedule.hour || 0) * 60 + (schedule.minute || 0);
        const hourUtc = (minute - timezoneOffset) / 60;
        if (hourUtc < 0) {
          day = schedule.day - 1;
          if (day === 0) {
            day = -1;
          }
        } else if (hourUtc >= 24) {
          day = schedule.day + 1;
        }
      }
      return {
        ...schedule,
        day,
        wday: wday === 7 ? 0 : wday,
        hour: getDateTime({
          hour: schedule.hour,
        })
          .setZone(timeZone, { keepLocalTime: true })
          .toUTC().hour,
        minute: getDateTime({
          minute: schedule.minute,
        })
          .setZone(timeZone, { keepLocalTime: true })
          .toUTC().minute,
      };
    });
}

export function convertScheduleToRRule(
  schedule: RoutineScheduleItem,
  routineStartedAt: string | Date,
  timeZone?: string,
) {
  const scheduleTime = getDateTime({
    hour: schedule.hour !== null ? schedule.hour : undefined,
    minute: schedule.minute !== null ? schedule.minute : undefined,
  });
  const repeatUnit = getRepeatUnit(
    schedule.monthInterval,
    schedule.dayInterval,
    schedule.wdayInterval,
  );
  let rule: RRule | null = null;
  const dtstart =
    typeof routineStartedAt === 'string'
      ? getDateTime(routineStartedAt, 'utc').startOf('minute').toJSDate()
      : routineStartedAt;
  switch (repeatUnit) {
    case 'monthly':
      rule = new RRule({
        freq: RRule.MONTHLY,
        interval: schedule.monthInterval,
        bymonthday: schedule.day,
        dtstart,
        byhour: scheduleTime.hour,
        byminute: scheduleTime.minute,
        bysecond: 0,
      });
      break;
    case 'weekly':
      rule = new RRule({
        freq: RRule.WEEKLY,
        interval: schedule.wdayInterval,
        wkst: RRule.SU,
        // 元データが日曜=0月曜=1だったものをRRuleの月曜=0〜日曜=6に変換する処理
        byweekday: schedule.wday ? schedule.wday - 1 : 6,
        dtstart,
        byhour: scheduleTime.hour,
        byminute: scheduleTime.minute,
        bysecond: 0,
      });
      break;
    case 'daily':
      rule = new RRule({
        freq: RRule.DAILY,
        interval: schedule.dayInterval,
        dtstart,
        byhour: scheduleTime.hour,
        byminute: scheduleTime.minute,
        bysecond: 0,
      });
      break;
    default:
      return;
  }

  return rule;
}

export function convertScheduleFormDataToRRule(
  schedule: ScheduleFormData,
  routineStartedAt: Date,
  timeZone?: string,
) {
  const routineSchedule: RoutineScheduleItem = {
    id: -1,
    ...schedule,
    timeZone: '',
    createdAt: '',
  };

  return convertScheduleToRRule(routineSchedule, routineStartedAt, timeZone);
}
